import classNames from 'classnames'
import React, { PropsWithChildren } from 'react'
import { InternalExternalLink } from '../../types/InternalExternalLink'
import {
  isNullOrWhiteSpace,
  sanitizeHtmlAndReplaceNewlines,
} from '../../utilities/string'
import { AnchorLink } from '../AnchorLink'
import { Button } from '../Button'
import { Image, ImageProps } from '../Image'

export interface TwoColumnBannerProps {
  image?: ImageProps | null | undefined
  link?: InternalExternalLink
  mainTitle?: string
  mainTitleLinkUrl?: string
  textContent?: string
  textTitle?: string
}

export const TwoColumnBanner = ({
  children,
  image,
  link,
  mainTitle,
  mainTitleLinkUrl,
  textContent,
  textTitle,
}: PropsWithChildren<TwoColumnBannerProps>) => {
  const hasChildren = React.Children.count(children) > 0
  const imageFilter = hasChildren ? 'sm:filter-grayscale' : ''

  return (
    <div className="bg-primary text-white tb:flex">
      <div className="flex-1 flex justify-center tb:justify-end">
        <div
          className={classNames(
            'h-full',
            'min-h-480',
            'grid',
            'grid-cols-mobile',
            'gap-x-30',
            'tb:grid-cols-tb-1/2',
            'sm:grid-cols-sm-1/2 sm:mr-30',
            'md:grid-cols-md-1/2 md:mr-0',
            'lg:grid-cols-lg-1/2',
            'xl:grid-cols-xl-1/2',
            'xxl:grid-cols-xxl-1/2'
          )}
        >
          <div
            className={classNames(
              'col-span-full pr-30',
              'tb:col-start-1 tb:col-span-2',
              'sm:col-start-2 sm:col-span-2 sm:pr-90',
              'md:col-start-1 md:col-span-2 md:pr-15',
              'lg:col-start-2 lg:col-span-2 lg:-mr-30 lg:pr-0',
              'xl:col-start-2 xl:col-span-3 xl:mr-0',
              'xxl:col-start-3 xxl:col-span-3'
            )}
          >
            <div
              className={classNames(
                'h-full flex flex-col justify-between',
                'pt-30 pb-45',
                'sm:pt-90 sm:pb-90',
                'xl:pb-120',
                'xxl:pb-150'
              )}
            >
              <h2 className="font-h2 text-h2">
                {mainTitleLinkUrl ? (
                  <AnchorLink
                    className="transition-hover hover:opacity-hover"
                    url={mainTitleLinkUrl}
                  >
                    {mainTitle}
                  </AnchorLink>
                ) : (
                  mainTitle
                )}
              </h2>
              <div className="mt-90">
                {textTitle && <h3 className="font-h3 text-h3">{textTitle}</h3>}

                {textContent && (
                  <p
                    className={classNames(
                      'mt-15 font-t-light text-t-300',
                      'sm:mt-30 sm:text-t-400',
                      'lg:text-t-500',
                      'xl:text-t-600'
                    )}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlAndReplaceNewlines(textContent),
                    }}
                  />
                )}

                {link && !isNullOrWhiteSpace(link.url) && (
                  <Button
                    isExternalUrl={link.isExternal}
                    url={link.url}
                    lightBorder
                    additionalClassName="mt-30"
                  >
                    {link.title}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          'flex-1',
          'min-h-480',
          'sm:min-h-[52.5rem]',
          'xl:min-h-[70.3125rem]',
          'xxl:min-h-[92.8125rem]',
          'tb:ml-30',
          'sm:-ml-30',
          'md:ml-30',
          'relative',
          'flex items-center justify-center',
          'xxl:justify-start'
        )}
      >
        {image && (
          <Image
            {...image}
            applyLqip
            caption=""
            className="absolute inset-0"
            copyright=""
            imgClassName={`w-full h-full object-cover ${imageFilter}`}
            loading="lazy"
          />
        )}
        {hasChildren && (
          <>
            {image && (
              <div className="absolute top-0 left-0 w-full h-full bg-primary blend-multiply hidden sm:block"></div>
            )}

            <div className="hidden sm:block relative flex-grow max-w-[31.875rem] xl:max-w-[37.5rem] mx-15 my-30 xxl:ml-330">
              {children}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
